import { useEffect, useRef, useCallback } from 'react';
import type { IChartApi, ISeriesApi, CandlestickData, Time } from 'lightweight-charts';

interface SwingPoint {
  time: number;
  price: number;
  type: 'high' | 'low';
  x?: number;
  y?: number;
}

interface ChartScanAnimationProps {
  chart: IChartApi | null;
  series: ISeriesApi<'Candlestick'> | null;
  isActive: boolean;
  duration?: number; // Duration of one scan cycle in ms
}

// Detect swing highs and lows from candle data
function detectSwingPoints(candles: CandlestickData<Time>[], lookback: number = 3): SwingPoint[] {
  const swings: SwingPoint[] = [];

  for (let i = lookback; i < candles.length - lookback; i++) {
    const current = candles[i];
    let isSwingHigh = true;
    let isSwingLow = true;

    // Check if current candle is a swing high/low
    for (let j = 1; j <= lookback; j++) {
      const before = candles[i - j];
      const after = candles[i + j];

      if (current.high <= before.high || current.high <= after.high) {
        isSwingHigh = false;
      }
      if (current.low >= before.low || current.low >= after.low) {
        isSwingLow = false;
      }
    }

    if (isSwingHigh) {
      swings.push({
        time: current.time as number,
        price: current.high,
        type: 'high',
      });
    }
    if (isSwingLow) {
      swings.push({
        time: current.time as number,
        price: current.low,
        type: 'low',
      });
    }
  }

  // Sort by time
  return swings.sort((a, b) => a.time - b.time);
}

const SCAN_PHASES = [
  { label: 'SCANNING PRICE ACTION', duration: 0.1 },
  { label: 'DETECTING STRUCTURE', duration: 0.2 },
  { label: 'MAPPING SWING POINTS', duration: 0.35 },
  { label: 'ANALYZING ORDER FLOW', duration: 0.5 },
  // Return trip phases
  { label: 'VALIDATING PATTERNS', duration: 0.6 },
  { label: 'CHECKING CONFLUENCES', duration: 0.7 },
  { label: 'CALCULATING ENTRY', duration: 0.8 },
  { label: 'FINALIZING SETUP', duration: 0.95 },
];

export default function ChartScanAnimation({
  chart,
  series,
  isActive,
  duration = 4000,
}: ChartScanAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const outerRafRef = useRef<number | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const currentPhaseRef = useRef(0);
  const swingPointsRef = useRef<SwingPoint[]>([]);
  const candleDataRef = useRef<CandlestickData<Time>[]>([]);

  // Get candle data and detect swing points
  useEffect(() => {
    if (!series || !isActive) return;

    const data = series.data() as CandlestickData<Time>[];
    candleDataRef.current = data;
    swingPointsRef.current = detectSwingPoints(data, 2);
  }, [series, isActive]);

  const draw = useCallback((timestamp: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !chart || !series || !isActive) {
      return;
    }

    if (startTimeRef.current === 0) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    // Loop continuously - use modulo to keep cycling
    const progress = (elapsed % duration) / duration;

    // Sync canvas internal size with parent element size every frame
    // Use parent dimensions since canvas with inset-0 should match parent
    const parent = canvas.parentElement;
    if (parent) {
      const rect = parent.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && (canvas.width !== rect.width || canvas.height !== rect.height)) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    }

    const { width, height } = canvas;

    // Skip drawing if canvas has no valid size yet
    if (width <= 0 || height <= 0) {
      animationRef.current = requestAnimationFrame(draw);
      return;
    }

    ctx.clearRect(0, 0, width, height);

    try {
    // Get time scale for coordinate conversions
    const timeScale = chart.timeScale();
    const visibleRange = timeScale.getVisibleLogicalRange();
    if (!visibleRange) {
      animationRef.current = requestAnimationFrame(draw);
      return;
    }

    const candleData = candleDataRef.current;
    if (candleData.length === 0) {
      animationRef.current = requestAnimationFrame(draw);
      return;
    }

    // Use the chart's visible time range to get scan boundaries
    const PRICE_SCALE_WIDTH = 55;
    let scanStartX = 5;
    let scanEndX = width - PRICE_SCALE_WIDTH;

    // Try to get exact coordinates from visible time range
    const visibleTimeRange = timeScale.getVisibleRange();
    if (visibleTimeRange) {
      const leftX = timeScale.timeToCoordinate(visibleTimeRange.from);
      const rightX = timeScale.timeToCoordinate(visibleTimeRange.to);
      if (leftX !== null && rightX !== null) {
        scanStartX = Math.max(5, leftX);
        scanEndX = Math.min(width - PRICE_SCALE_WIDTH, rightX);
      }
    }

    const scanRange = scanEndX - scanStartX;

    // Calculate scan line position with ping-pong effect
    // First half: go forward (left to right), Second half: go backward (right to left)
    let scanX: number;
    let isReturning: boolean;
    if (progress <= 0.5) {
      // Going forward
      scanX = scanStartX + (progress * 2) * scanRange;
      isReturning = false;
    } else {
      // Going backward
      scanX = scanStartX + (1 - (progress - 0.5) * 2) * scanRange;
      isReturning = true;
    }

    // Update phase based on progress
    const newPhase = SCAN_PHASES.findIndex((p, i) => {
      const next = SCAN_PHASES[i + 1];
      return progress >= p.duration && (!next || progress < next.duration);
    });
    if (newPhase !== -1) {
      currentPhaseRef.current = newPhase;
    }

    // Draw semi-transparent overlay on unscanned area (only on chart area, not price scale)
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    if (isReturning) {
      // When returning, overlay is on the left side (from start to scan line)
      ctx.fillRect(scanStartX, 0, scanX - scanStartX, height);
    } else {
      // When going forward, overlay is on the right side (from scan line to end of chart area)
      ctx.fillRect(scanX, 0, scanEndX - scanX, height);
    }
    ctx.restore();

    // Draw vertical scan line with glow
    ctx.save();

    // Glow effect
    const gradient = ctx.createLinearGradient(scanX - 30, 0, scanX + 30, 0);
    gradient.addColorStop(0, 'transparent');
    gradient.addColorStop(0.3, 'rgba(0, 255, 136, 0.1)');
    gradient.addColorStop(0.5, 'rgba(0, 255, 136, 0.3)');
    gradient.addColorStop(0.7, 'rgba(0, 255, 136, 0.1)');
    gradient.addColorStop(1, 'transparent');
    ctx.fillStyle = gradient;
    ctx.fillRect(scanX - 30, 0, 60, height);

    // Main scan line
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur = 20;
    ctx.beginPath();
    ctx.moveTo(scanX, 0);
    ctx.lineTo(scanX, height);
    ctx.stroke();

    // Secondary cyan line
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 1;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.moveTo(scanX + 3, 0);
    ctx.lineTo(scanX + 3, height);
    ctx.stroke();

    ctx.restore();

    // Draw scanned candle highlights
    const highlightedCandles: Array<{ x: number; y: number; color: string; dist: number }> = [];

    for (let i = 0; i < candleData.length; i++) {
      const candle = candleData[i];
      const x = timeScale.timeToCoordinate(candle.time);
      if (x === null) continue;

      // Check if candle is in scanned area
      const isInScannedArea = isReturning ? (x > scanX) : (x < scanX);
      if (!isInScannedArea) continue;

      const highY = series.priceToCoordinate(candle.high);
      const lowY = series.priceToCoordinate(candle.low);
      if (highY === null || lowY === null) continue;

      // Only highlight candles near the scan line
      const distFromScanLine = Math.abs(scanX - x);
      if (distFromScanLine < 50) {
        const isGreen = candle.close >= candle.open;
        highlightedCandles.push({
          x,
          y: isGreen ? highY : lowY,
          color: isGreen ? '#22c55e' : '#ef4444',
          dist: distFromScanLine,
        });
      }
    }

    // Draw candle highlight particles
    ctx.save();
    highlightedCandles.forEach(({ x, y, color, dist }) => {
      const alpha = Math.max(0, 1 - dist / 50);
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha * 0.8;
      ctx.shadowColor = color;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();

    // Draw trend lines progressively
    const swings = swingPointsRef.current;
    const swingsToShow = swings.filter(swing => {
      const x = timeScale.timeToCoordinate(swing.time as Time);
      if (x === null) return false;
      // Show swings in the scanned area
      return isReturning ? (x >= scanX) : (x <= scanX);
    });

    // Calculate coordinates for visible swings
    swingsToShow.forEach(swing => {
      const x = timeScale.timeToCoordinate(swing.time as Time);
      const y = series.priceToCoordinate(swing.price);
      swing.x = x !== null ? x : undefined;
      swing.y = y !== null ? y : undefined;
    });

    // Draw swing points and connecting lines
    ctx.save();

    // Separate highs and lows
    const highs = swingsToShow.filter(s => s.type === 'high' && s.x !== undefined && s.y !== undefined);
    const lows = swingsToShow.filter(s => s.type === 'low' && s.x !== undefined && s.y !== undefined);

    // Draw trend line for highs (resistance)
    if (highs.length >= 2) {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 1.5;
      ctx.shadowColor = '#ef4444';
      ctx.shadowBlur = 8;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();

      // Connect last few swing highs
      const recentHighs = highs.slice(-4);
      recentHighs.forEach((swing, i) => {
        if (i === 0) {
          ctx.moveTo(swing.x!, swing.y!);
        } else {
          ctx.lineTo(swing.x!, swing.y!);
        }
      });
      ctx.stroke();

      // Draw swing high markers
      ctx.setLineDash([]);
      recentHighs.forEach(swing => {
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(swing.x!, swing.y!, 4, 0, Math.PI * 2);
        ctx.fill();

        // Small "HH" or "LH" label
        ctx.font = 'bold 9px JetBrains Mono, monospace';
        ctx.fillStyle = '#ef4444';
        ctx.textAlign = 'center';
        ctx.fillText('H', swing.x!, swing.y! - 10);
      });
    }

    // Draw trend line for lows (support)
    if (lows.length >= 2) {
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 1.5;
      ctx.shadowColor = '#22c55e';
      ctx.shadowBlur = 8;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();

      // Connect last few swing lows
      const recentLows = lows.slice(-4);
      recentLows.forEach((swing, i) => {
        if (i === 0) {
          ctx.moveTo(swing.x!, swing.y!);
        } else {
          ctx.lineTo(swing.x!, swing.y!);
        }
      });
      ctx.stroke();

      // Draw swing low markers
      ctx.setLineDash([]);
      recentLows.forEach(swing => {
        ctx.fillStyle = '#22c55e';
        ctx.beginPath();
        ctx.arc(swing.x!, swing.y!, 4, 0, Math.PI * 2);
        ctx.fill();

        // Small "HL" or "LL" label
        ctx.font = 'bold 9px JetBrains Mono, monospace';
        ctx.fillStyle = '#22c55e';
        ctx.textAlign = 'center';
        ctx.fillText('L', swing.x!, swing.y! + 15);
      });
    }

    ctx.restore();

    // Draw floating data indicators near scan line
    ctx.save();
    const indicatorY = height * 0.15;
    const pulse = Math.sin(timestamp / 200) * 0.3 + 0.7;

    // Position indicator on the scanned side of the line
    const indicatorOffset = isReturning ? -15 : 15;
    const textAlign = isReturning ? 'right' : 'left';
    const indicatorX = scanX + indicatorOffset;

    // Current phase label
    const phase = SCAN_PHASES[Math.min(currentPhaseRef.current, SCAN_PHASES.length - 1)];
    ctx.font = 'bold 11px JetBrains Mono, monospace';
    ctx.fillStyle = `rgba(0, 255, 136, ${pulse})`;
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur = 10;
    ctx.textAlign = textAlign;
    ctx.fillText(phase.label, indicatorX, indicatorY);

    // Progress percentage
    ctx.font = '10px JetBrains Mono, monospace';
    ctx.fillStyle = `rgba(0, 255, 255, ${pulse})`;
    ctx.shadowColor = '#00ffff';
    ctx.fillText(`${Math.round(progress * 100)}%`, indicatorX, indicatorY + 18);

    // Small data snippets appearing near scan line
    if (progress > 0.2) {
      ctx.font = '9px JetBrains Mono, monospace';
      ctx.fillStyle = 'rgba(148, 163, 184, 0.7)';
      ctx.shadowBlur = 0;

      const snippets = [
        `SWINGS: ${swingsToShow.length}`,
        `H: ${highs.length} L: ${lows.length}`,
      ];

      snippets.forEach((text, i) => {
        const snippetY = indicatorY + 40 + i * 14;
        ctx.fillText(text, indicatorX, snippetY);
      });
    }

    ctx.restore();

    // Draw large neon corner brackets - FIXED to full canvas size with 4px padding
    ctx.save();
    const PADDING = 4;
    const bracketLength = 100; // Length of each bracket line
    const bracketLeft = PADDING;
    const bracketRight = width - PADDING; // Full width, ignore price scale - canvas handles it
    const bracketTop = PADDING;
    const bracketBottom = height - PADDING;

    // Create gradient for horizontal lines
    const createHGradient = (startX: number, endX: number, toRight: boolean) => {
      const grad = ctx.createLinearGradient(startX, 0, endX, 0);
      if (toRight) {
        grad.addColorStop(0, `rgba(0, 255, 136, ${pulse})`);
        grad.addColorStop(0.5, `rgba(0, 255, 136, ${pulse * 0.5})`);
        grad.addColorStop(1, 'transparent');
      } else {
        grad.addColorStop(0, 'transparent');
        grad.addColorStop(0.5, `rgba(0, 255, 136, ${pulse * 0.5})`);
        grad.addColorStop(1, `rgba(0, 255, 136, ${pulse})`);
      }
      return grad;
    };

    // Create gradient for vertical lines
    const createVGradient = (startY: number, endY: number, toBottom: boolean) => {
      const grad = ctx.createLinearGradient(0, startY, 0, endY);
      if (toBottom) {
        grad.addColorStop(0, `rgba(0, 255, 136, ${pulse})`);
        grad.addColorStop(0.5, `rgba(0, 255, 136, ${pulse * 0.5})`);
        grad.addColorStop(1, 'transparent');
      } else {
        grad.addColorStop(0, 'transparent');
        grad.addColorStop(0.5, `rgba(0, 255, 136, ${pulse * 0.5})`);
        grad.addColorStop(1, `rgba(0, 255, 136, ${pulse})`);
      }
      return grad;
    };

    ctx.lineWidth = 2;
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur = 15;

    // Top-left corner
    ctx.strokeStyle = createHGradient(bracketLeft, bracketLeft + bracketLength, true);
    ctx.beginPath();
    ctx.moveTo(bracketLeft, bracketTop);
    ctx.lineTo(bracketLeft + bracketLength, bracketTop);
    ctx.stroke();

    ctx.strokeStyle = createVGradient(bracketTop, bracketTop + bracketLength, true);
    ctx.beginPath();
    ctx.moveTo(bracketLeft, bracketTop);
    ctx.lineTo(bracketLeft, bracketTop + bracketLength);
    ctx.stroke();

    // Top-right corner
    ctx.strokeStyle = createHGradient(bracketRight - bracketLength, bracketRight, false);
    ctx.beginPath();
    ctx.moveTo(bracketRight - bracketLength, bracketTop);
    ctx.lineTo(bracketRight, bracketTop);
    ctx.stroke();

    ctx.strokeStyle = createVGradient(bracketTop, bracketTop + bracketLength, true);
    ctx.beginPath();
    ctx.moveTo(bracketRight, bracketTop);
    ctx.lineTo(bracketRight, bracketTop + bracketLength);
    ctx.stroke();

    // Bottom-left corner
    ctx.strokeStyle = createHGradient(bracketLeft, bracketLeft + bracketLength, true);
    ctx.beginPath();
    ctx.moveTo(bracketLeft, bracketBottom);
    ctx.lineTo(bracketLeft + bracketLength, bracketBottom);
    ctx.stroke();

    ctx.strokeStyle = createVGradient(bracketBottom - bracketLength, bracketBottom, false);
    ctx.beginPath();
    ctx.moveTo(bracketLeft, bracketBottom - bracketLength);
    ctx.lineTo(bracketLeft, bracketBottom);
    ctx.stroke();

    // Bottom-right corner
    ctx.strokeStyle = createHGradient(bracketRight - bracketLength, bracketRight, false);
    ctx.beginPath();
    ctx.moveTo(bracketRight - bracketLength, bracketBottom);
    ctx.lineTo(bracketRight, bracketBottom);
    ctx.stroke();

    ctx.strokeStyle = createVGradient(bracketBottom - bracketLength, bracketBottom, false);
    ctx.beginPath();
    ctx.moveTo(bracketRight, bracketBottom - bracketLength);
    ctx.lineTo(bracketRight, bracketBottom);
    ctx.stroke();

    ctx.restore();

    // Continue animation loop (will stop when isActive becomes false)
    animationRef.current = requestAnimationFrame(draw);
    } catch {
      // Chart may have been removed (e.g. Mobile Safari navigation during scan)
    }
  }, [chart, series, isActive, duration]);

  // Set up canvas size and start animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      // Use parent element dimensions since canvas with inset-0 should match parent
      const parent = canvas.parentElement;
      if (parent) {
        const rect = parent.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          canvas.width = rect.width;
          canvas.height = rect.height;
        }
      }
    };

    // Use ResizeObserver for reliable size tracking
    const parent = canvas.parentElement;
    let resizeObserver: ResizeObserver | null = null;

    if (parent) {
      resizeObserver = new ResizeObserver(() => {
        resizeCanvas();
      });
      resizeObserver.observe(parent);
    }

    // Initial resize - use requestAnimationFrame to ensure layout is computed
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      resizeCanvas();
      // Also try after a short delay as fallback for late layout
      resizeTimerRef.current = setTimeout(() => {
        resizeTimerRef.current = null;
        resizeCanvas();
      }, 50);
    });

    // Start animation loop when active
    if (isActive) {
      startTimeRef.current = 0;
      currentPhaseRef.current = 0;
      // Small delay to ensure canvas has valid dimensions from layout.
      // Track the outer RAF ID so cleanup can cancel it if the component
      // unmounts before it fires (otherwise the inner RAF becomes orphaned
      // and runs draw() on a removed chart → "Value is null" on Mobile Safari).
      outerRafRef.current = requestAnimationFrame(() => {
        outerRafRef.current = null;
        animationRef.current = requestAnimationFrame(draw);
      });
    }

    return () => {
      resizeObserver?.disconnect();
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      if (resizeTimerRef.current !== null) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
      if (outerRafRef.current !== null) {
        cancelAnimationFrame(outerRafRef.current);
        outerRafRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [draw, isActive]);

  // Reset when becoming inactive
  useEffect(() => {
    if (!isActive) {
      startTimeRef.current = 0;
      currentPhaseRef.current = 0;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Clear canvas
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [isActive]);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 15 }}
    />
  );
}
